<template>
	<div class="h-full flex flex-col">
		<!-- Loading state -->
		<div v-if="loading" class="flex items-center justify-center py-12">
			<span class="loading loading-spinner loading-md"></span>
		</div>

		<!-- Empty state -->
		<div v-else-if="lessonPlans.length === 0" class="flex items-center justify-center py-12">
			<p class="text-sm text-base-content/60">{{ $t('This collection is empty') }}</p>
		</div>

		<LessonPlanList
			v-else
			:lessonPlans="lessonPlans"
			:withHeader="false"
			:withLibraryStatus="!isAgendaContext() && usersStore.me?.id !== authorId"
			:showCreatorName="false"
			:page="lessonPlanPage || undefined"
			:loading="loading"
			@lessonPlanClicked="openLessonPlan($event)"
			@requestPage="goToPage"
			@loadMore="loadMorePlans">
		</LessonPlanList>
	</div>

	<LessonPlanViewer
		v-if="showLessonPlanViewer"
		:lessonPlan="selectedLessonPlan"
		:withSelectButtons="isAgendaContext()"
		:initialWidth="isAgendaContext() ? '2xl' : '3xl'"
		:withLibraryButton="!isAgendaContext()"
		@requestUnmount="closeLessonPlanViewer"
		@selectLessonPlan="$emit('selectLessonPlan', $event)"
		@createVariant="onCreateVariant"
	/>

	<LessonPlanEditor
		v-if="showEditor"
		:lessonPlan="editingLessonPlan"
		context="library"
		:skipOpenAnimation="skipEditorAnimation"
		:suggestedGoals="suggestedGoals"
		@requestClose="closeEditor"
		@cancel="handleEditorCancel"
		@saved="handleEditorSaved"
	/>
</template>

<script lang="ts">
import { mapStores } from 'pinia';
import { useLessonPlanCollectionsStore } from '@/stores/LessonPlanCollections.store';
import { useDiscoverStateStore } from '@/stores/DiscoverCollections.store';
import LessonPlanViewer from '@/components/LessonPlanViewer.vue';
import { useLessonPlansStore } from '@/stores/LessonPlans.store';
import { useUsersStore } from '@/stores/Users.store';
import { LessonPlan } from '@/models/LessonPlan.model';
import { PaginationState } from '@/models/PaginationState.model';
import { ApiErrorMessageBag } from '@/stores/errors/ApiErrorMessageBag';
import { notify } from 'notiwind';
import LessonPlanList from './LessonPlanList.vue';
import { OrderDirection, OrderParameter } from '@/models/OrderParameter.model';
import LessonPlanEditor from '@/components/LessonPlanEditor.vue';
import { isAgendaRoute } from "@/utils/RouteContext.util";

export default {

	name: 'LessonPlanDiscoverAuthorCollection',
	emits: ['selectLessonPlan', 'createVariant'],

	components: {
		LessonPlanViewer,
		LessonPlanList,
		LessonPlanEditor,
	},

	props: {
		collectionId: {
			type: String,
			required: true,
		},
		authorId: {
			type: String,
			required: true,
		},
	},

	data() {
		return {
			loading: false,
			lessonPlans: [] as LessonPlan[],
			lessonPlanPage: null as PaginationState | null,
			showLessonPlanViewer: false,
			selectedLessonPlan: undefined as LessonPlan | undefined,
			showEditor: false,
			editingLessonPlan: undefined as LessonPlan | undefined,
			skipEditorAnimation: false,
			suggestedGoals: [] as any[],
			pendingVariant: undefined as LessonPlan | undefined,
		};
	},

	async created() {
		await this.loadPlans();
	},

	computed: {
		...mapStores(useLessonPlansStore, useDiscoverStateStore, useLessonPlanCollectionsStore, useUsersStore),
	},

	methods: {
		isAgendaContext(): boolean {
			return isAgendaRoute(this.$router);
		},

		onCreateVariant(lessonPlan: LessonPlan) {
			// In the agenda, LessonActivities has its own flow for a variant (it also assigns
			// the result to the lesson item): just pass the request on.
			if (this.isAgendaContext()) {
				this.$emit('createVariant', lessonPlan);
				return;
			}

			// Don't close the viewer here — it's already animating itself closed
			// (see LessonPlanViewer.confirmCreateVariant). We just stash the data
			// and let closeLessonPlanViewer (fired by @requestUnmount once the
			// animation actually finishes) open the editor.
			this.pendingVariant = lessonPlan.createVariantFor(this.usersStore.me);
		},

		closeEditor() {
			this.showEditor = false;
			this.editingLessonPlan = undefined;
		},

		handleEditorCancel() {
			// No "original" to fall back to for a new variant — just close
			this.closeEditor();
		},

		handleEditorSaved() {
			// Don't close here — LessonPlanEditor.save() already calls animatedClose()
			// itself, which animates out and then emits requestClose → closeEditor().
			// The variant went to My Library while the user stays here: tell them where it is.
			this.$notify({ text: this.$t('The variant was saved to My Library'), type: 'success' });
		},

		async loadPlans() {
			if (!this.collectionId) return;
			this.loading = true;
			try {
				// Fixed alphabetical ordering in this specific case
				const order = new OrderParameter('title', OrderDirection.ASC);
				const result = await this.lessonPlansStore.loadByCollection(this.collectionId, this.lessonPlanPage, order);
				this.lessonPlans = result.items;
				this.lessonPlanPage = result.page;
			} catch (e: any) {
				if (e instanceof ApiErrorMessageBag) {
					notify({
						text: e.message || this.$t('An error occurred'),
						type: 'error',
					});
				}
			} finally {
				this.loading = false;
			}
		},

		async goToPage(page: PaginationState) {
			this.lessonPlanPage = page;
			await this.loadPlans();
		},

		async loadMorePlans() {
			if (this.loading || !this.lessonPlanPage?.hasNext()) return;
			this.loading = true;
			try {
				const nextPage = this.lessonPlanPage.getNext();
				const result = await this.lessonPlansStore.loadByCollection(this.collectionId, nextPage);
				this.lessonPlans.push(...result.items);
				this.lessonPlanPage = result.page;
				// Adjust 'from' to reflect all loaded items
				if (this.lessonPlanPage) {
					this.lessonPlanPage.from = 1;
				}
			} catch (e: any) {
				if (e instanceof ApiErrorMessageBag) {
					notify({
						text: e.message || this.$t('An error occurred'),
						type: 'error',
					});
				}
			} finally {
				this.loading = false;
			}
		},

		openLessonPlan(lessonPlan: LessonPlan) {
			this.selectedLessonPlan = lessonPlan;
			this.showLessonPlanViewer = true;
		},

		closeLessonPlanViewer() {
			this.showLessonPlanViewer = false;
			this.selectedLessonPlan = undefined;

			if (this.pendingVariant) {
				this.editingLessonPlan = this.pendingVariant;
				this.pendingVariant = undefined;
				this.showEditor = true;
			}
		},
	},
};
</script>
